import React, { useRef } from 'react';
import { ScissorsIcon, LayersIcon, SmileIcon, SparklesIcon, UsersIcon, PaletteIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
type ServiceCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
};
function ServiceCard({
  icon,
  title,
  description,
  delay
}: ServiceCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay
  }} className="group relative bg-[#1c1c1c] border border-[#2a2a2a] rounded-lg p-6 hover:border-[#c41e3a]/50 transition-all duration-300" data-id="element-227">
      {/* Red accent line on hover */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#c41e3a] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-t-lg" aria-hidden="true" data-id="element-228" />

      <div className="flex items-start gap-4" data-id="element-229">
        <div className="flex-shrink-0 w-12 h-12 bg-[#c41e3a]/10 border border-[#c41e3a]/20 rounded-lg flex items-center justify-center text-[#c41e3a]" data-id="element-230">
          {icon}
        </div>
        <div data-id="element-231">
          <h3 className="text-white font-bold text-xl mb-1" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.04em'
        }} data-id="element-232">
            {title}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-233">{description}</p>
        </div>
      </div>
    </motion.div>;
}
export function Services() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, {
    once: true,
    margin: '-60px'
  });
  return <section id="services" className="w-full bg-[#0d0d0d] py-20 md:py-28 px-4" data-id="element-234">
      <div className="max-w-6xl mx-auto" data-id="element-235">
        {/* Section Header */}
        <motion.div ref={headerRef} initial={{
        opacity: 0,
        y: 30
      }} animate={headerInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-236">
          <p className="text-[#c41e3a] font-semibold tracking-[0.25em] uppercase text-sm mb-3" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-237">
            What We Offer
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-238">
            OUR SERVICES
          </h2>
          <div className="section-divider w-24 mx-auto" data-id="element-239" />
          <p className="text-gray-400 mt-6 text-lg max-w-xl mx-auto" data-id="element-240">
            We don't just cut hair. We build confidence. Every man who sits in
            our chair leaves looking better than when he walked in.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-241">
          <ServiceCard icon={<ScissorsIcon size={22} data-id="element-243" />} title="Haircuts" description="Classic, modern, textured — any style you want, executed with precision." delay={0} data-id="element-242" />
          <ServiceCard icon={<LayersIcon size={22} data-id="element-245" />} title="Tapers & Fades" description="Skin fades, low, mid and high — clean transitions every single time." delay={0.08} data-id="element-244" />
          <ServiceCard icon={<SmileIcon size={22} data-id="element-247" />} title="Beard Shaves & Shapeups" description="Clean lines, full shaves, and beard sculpting done right." delay={0.16} data-id="element-246" />
          <ServiceCard icon={<SparklesIcon size={22} data-id="element-249" />} title="Eyebrow Design" description="Sharp, defined, and natural-looking brow shaping for a polished finish." delay={0.24} data-id="element-248" />
          <ServiceCard icon={<UsersIcon size={22} data-id="element-251" />} title="Girls Cuts" description="Everyone is welcome at Jonathan's — great cuts for all ages." delay={0.32} data-id="element-250" />
          <ServiceCard icon={<PaletteIcon size={22} data-id="element-253" />} title="Custom Designs" description="Hair art and custom designs — express yourself with precision line work." delay={0.4} data-id="element-252" />
        </div>
      </div>
    </section>;
}